// Packages and components
import React from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInput from '@ui/Input/SHDInput';
import SHDLoader from '@ui/Loader/SHDLoader';
import SHDText from '@ui/Text/SHDText';

// Helpers and utilities
import { useAdminUserSearch } from '@hooks/useAdmin';

// Types
import type {
  AdminUserSearchProps,
  FirebaseUserSearchResult,
} from '../../admin.types';

const AdminUserSearch: React.FC<AdminUserSearchProps> = ({ onContinue }) => {
  const { debouncing, loading, onClearResults, onSearch, results } =
    useAdminUserSearch();

  // Handlers
  const handleInputChange = (query: string) => {
    if (query === '') {
      onClearResults();
      return;
    }
    onSearch(query);
  };

  const handleSelectUser = (result: FirebaseUserSearchResult) => {
    onContinue && onContinue(result);
    onClearResults();
    handleInputChange('');
  };

  // Renderers
  const renderSearchResults = (
    result: FirebaseUserSearchResult,
    index?: number
  ) => {
    return (
      <Box
        key={index}
        background="gray-50"
        direction="row"
        align="center"
        justify="between"
        round="medium"
        pad="large"
      >
        <Box>
          <SHDText style="subhead-bold">{result?.name}</SHDText>
          <Box direction="row" wrap={false} gap="small">
            <SHDText style="subhead" color="gray-600">
              {result?.id}
            </SHDText>
          </Box>
          <Box direction="row" wrap={false} gap="small">
            {result?.email && (
              <SHDText style="subhead" color="gray-600">
                {result?.email}
              </SHDText>
            )}
            {result?.phoneList && (
              <SHDText style="subhead" color="gray-600">
                {result?.phoneList.map((phone) => phone).join(', ')}
              </SHDText>
            )}
          </Box>
        </Box>
        <Box width="5.625rem">
          <SHDButton
            tertiary
            label={'Select'}
            onClick={() => handleSelectUser(result)}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box className="advanced-user-settings__search" margin={{ top: 'large' }}>
      <Box gap="xsmall">
        <SHDHeading level={2}>Advanced User Settings</SHDHeading>
        <SHDInput label="User ID" onChange={handleInputChange} />
      </Box>
      <Box margin={{ top: 'medium' }}>
        {debouncing || loading ? (
          <Box width="100%" align="center">
            <SHDLoader size={24} color="brand-800" />
          </Box>
        ) : (
          <>
            {results?.length &&
              results.map((result, index) => (
                <>{renderSearchResults(result, index)}</>
              ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminUserSearch;
